import Papa from "papaparse";

import { coerceValue, normalizeHeader, headerMatches } from "./data-parser";

/**
 * @fileoverview Thin wrapper around PapaParse for clipboard data parsing.
 * PapaParse handles all delimiter detection and edge cases automatically.
 * We just add header mapping and type coercion.
 */

export interface ParsedPasteData<T> {
  rows: Partial<T>[];
  startRowIndex: number;
}

export interface ClipboardParseOptions<T> {
  columns: Array<{
    key: keyof T;
    type?: "text" | "number" | "boolean" | "array";
    header?: string;
    headerAliases?: string[];
  }>;
  startRowIndex?: number;
  hasHeaders?: boolean;
  /**
   * Map cells to columns by index instead of by header name. A leading header
   * row is still detected and dropped: the caller has already decided which
   * column the paste starts at, so the header names must not move it.
   */
  mapByPosition?: boolean;
}

/**
 * Parse clipboard data using PapaParse.
 * Handles: delimiter detection, quoted fields, escaped characters, newlines in cells,
 * and skewed Excel worksheets with malformed delimiters.
 *
 * **Header row:** the first row is a header row when at least one of its cells
 * names a column outright (its header or one of its aliases). A header row is
 * dropped rather than pasted as data.
 *
 * **Column mapping:** by header name when the header row names most of the
 * pasted columns and the caller did not ask for `mapByPosition`; otherwise by
 * position. Mapping the leftovers of a barely-recognised header row by name
 * would silently drop every column it failed to name, which is why the
 * majority rule exists.
 *
 * Empty cells are preserved at their positions (important for sparse data):
 * if a row has [val1, "", val3], the empty string at position 1 is kept.
 */
export function parseClipboardData<T extends Record<string, unknown>>(
  clipboardText: string,
  options: ClipboardParseOptions<T>,
): ParsedPasteData<T> {
  const {
    columns,
    startRowIndex = 0,
    hasHeaders = true,
    mapByPosition = false,
  } = options;

  if (!clipboardText || clipboardText.trim() === "") {
    return { rows: [], startRowIndex };
  }

  // Let PapaParse auto-detect delimiter for best results
  // It handles tab, semicolon, comma, and pipe delimiters
  const parseResult = Papa.parse<string[]>(clipboardText, {
    delimiter: "", // Empty string enables auto-detection
    delimitersToGuess: ["\t", ";", ",", "|"], // Prioritize tab and semicolon over comma
    header: false,
    skipEmptyLines: "greedy", // Skip all empty lines including whitespace-only
    dynamicTyping: false, // We handle type coercion ourselves
    transform: (value) => value.trim(), // Trim whitespace from all cells
  });

  let allRows = parseResult.data as string[][];
  if (allRows.length === 0) {
    return { rows: [], startRowIndex };
  }

  // Edge case detection: Check if all data is in the first cell of each row
  // This happens when Excel opens a CSV but doesn't parse it correctly
  // Detect which delimiter is most common in the first cells
  const possibleDelimiters = [";", ",", "\t", "|"];
  let needsReparse = false;
  let detectedDelimiter = "";

  if (
    allRows.length > 0 &&
    allRows.every((row) => row.length === 1 && row[0])
  ) {
    // Count occurrences of each delimiter in first cells
    const delimiterCounts = possibleDelimiters.map((delim) => ({
      delim,
      count: allRows.reduce((sum, row) => {
        return sum + (row[0].split(delim).length - 1);
      }, 0),
    }));

    // Find the most common delimiter
    const mostCommon = delimiterCounts.reduce(
      (prev, curr) => (curr.count > prev.count ? curr : prev),
      { delim: "", count: 0 },
    );

    // If we found a delimiter used at least once, re-parse
    if (mostCommon.count > 0) {
      needsReparse = true;
      detectedDelimiter = mostCommon.delim;
    }
  }

  if (needsReparse && detectedDelimiter) {
    // Re-parse with detected delimiter
    const reparseResult = Papa.parse<string[]>(clipboardText, {
      delimiter: detectedDelimiter,
      header: false,
      skipEmptyLines: "greedy",
      dynamicTyping: false,
      transform: (value) => value.trim(),
    });
    allRows = reparseResult.data as string[][];
  }

  // Remove completely empty rows but preserve cell positions within rows
  const cleanedRows = allRows.filter((row) =>
    row.some((cell) => cell && cell.trim() !== ""),
  );

  if (cleanedRows.length === 0) {
    return { rows: [], startRowIndex };
  }

  // Separate headers from data
  const firstRow = hasHeaders
    ? matchHeaderCells(cleanedRows[0], columns)
    : { mapping: new Map<number, keyof T>(), namedColumns: 0 };
  // only a cell that names a column outright makes the row a header row; a
  // fuzzy hit like the value "IBAN rekening" is a data cell, not a header
  const isHeaderRow = firstRow.namedColumns > 0;
  // a header row only a few of whose cells were recognised cannot carry the
  // mapping: the unrecognised cells would map to nothing and their data would
  // be dropped, so fall back to position for the whole row
  const namesMostColumns =
    firstRow.mapping.size * 2 >= countNonEmpty(cleanedRows[0]);

  const dataRows = isHeaderRow ? cleanedRows.slice(1) : cleanedRows;
  const columnMapping =
    isHeaderRow && namesMostColumns && !mapByPosition
      ? firstRow.mapping
      : positionMapping(columns);

  // Build column type lookup
  const columnTypes = new Map<
    keyof T,
    "text" | "number" | "boolean" | "array"
  >();
  columns.forEach((col) => {
    if (col.type) columnTypes.set(col.key, col.type);
  });

  // Transform rows: map cells to columns and coerce types
  const rows: Partial<T>[] = dataRows.map((cells) => {
    const row: Partial<T> = {};

    // Process all possible column positions
    columnMapping.forEach((key, cellIndex) => {
      // Get cell value - may be undefined if row has fewer cells than expected
      const cellValue = cellIndex < cells.length ? cells[cellIndex] : "";
      const type = columnTypes.get(key);
      row[key] = coerceValue(cellValue, type) as T[keyof T];
    });

    return row;
  });

  return { rows, startRowIndex };
}

/**
 * Map each cell of a candidate header row to the column it refers to, and count
 * how many of those cells name a column outright rather than fuzzily.
 */
function matchHeaderCells<T extends Record<string, unknown>>(
  headerRow: string[],
  columns: ClipboardParseOptions<T>["columns"],
): { mapping: Map<number, keyof T>; namedColumns: number } {
  const mapping = new Map<number, keyof T>();
  let namedColumns = 0;

  headerRow.forEach((headerText, cellIndex) => {
    const key = findColumnKey(headerText, columns);
    if (!key) return;
    mapping.set(cellIndex, key);
    if (findExactColumnKey(headerText, columns)) namedColumns++;
  });

  return { mapping, namedColumns };
}

function countNonEmpty(row: string[]): number {
  return row.filter((cell) => cell.trim() !== "").length;
}

function positionMapping<T extends Record<string, unknown>>(
  columns: ClipboardParseOptions<T>["columns"],
): Map<number, keyof T> {
  const mapping = new Map<number, keyof T>();
  columns.forEach((col, index) => mapping.set(index, col.key));
  return mapping;
}

/**
 * Find column key matching a header text using aliases and fuzzy matching.
 */
function findColumnKey<T extends Record<string, unknown>>(
  headerText: string,
  columns: Array<{
    key: keyof T;
    header?: string;
    headerAliases?: string[];
  }>,
): keyof T | null {
  const exact = findExactColumnKey(headerText, columns);
  if (exact) return exact;

  // Try substring match
  const normalized = normalizeHeader(headerText);
  for (const col of columns) {
    const colHeader = normalizeHeader(col.header || String(col.key));
    if (colHeader.includes(normalized) || normalized.includes(colHeader)) {
      return col.key;
    }
  }

  return null;
}

/**
 * Find the column a header text names outright, through its own header or one
 * of its aliases.
 */
function findExactColumnKey<T extends Record<string, unknown>>(
  headerText: string,
  columns: Array<{
    key: keyof T;
    header?: string;
    headerAliases?: string[];
  }>,
): keyof T | null {
  for (const col of columns) {
    if (
      headerMatches(
        headerText,
        col.header || String(col.key),
        col.headerAliases,
      )
    ) {
      return col.key;
    }
  }

  return null;
}
